// Auto-generated from Skills.md
export const skillsContent = `---
name: aible
description: Aible MCP analyst workflow—after every aible_* tool call, read ~/.openclaw/aible-traces/current/outputs.json; start with blueprint_search, fall back to search_context, then submit_final_report.
---

# Aible Analyst Workflow

## CRITICAL RULE — READ BEFORE EVERY NEXT STEP

OpenClaw compacts tool call outputs in the conversation view to save context.
This means **you will never be able to see Aible tool results in the
conversation**. They are always hidden.

The plugin writes every tool output to:
  ~/.openclaw/aible-traces/current/outputs.json

**After EVERY single Aible tool call, you MUST run:**
\`\`\`
cat ~/.openclaw/aible-traces/current/outputs.json
\`\`\`

This is not optional. It is the only way to see what the tool returned.

- Never say "the output is compacted" and proceed anyway — that is an error.
- Never fabricate, guess, or paraphrase a result you haven\'t read from this file.
- Never move to the next step without first running the cat and reading the output.
- If the file is empty or missing, something went wrong — stop and report the error.

---

### Step 1: Blueprint search (always first)

Call \`aible_blueprint_search\` with the user\'s query and \`limit=1\`.

**Immediately after the call, run:**
\`\`\`
cat ~/.openclaw/aible-traces/current/outputs.json
\`\`\`
Do not proceed until you have run this command and read the output.

Inspect the last entry — a list of dicts. Each valid hit contains \`blueprint_id\`,
\`name\`, \`description\`, and **exactly one** of \`project_id\`, \`document_set_id\`,
or \`dataset_id\`.

**A row is invalid if it has zero or more than one of those id fields — treat it
as no result and fall back.**

Determine relevance using \`name\` and \`description\` against the user\'s question
and the tool branch you intend to use.

---

### Step 2a: Blueprint path (relevant hit found)

Use the populated id field to route. Each branch ends with a cat and then **go directly to Step 3**:

**\`project_id\` branch:**
Call \`aible_prediction_tool(project_id, query, blueprint_id)\`.
Run \`cat ~/.openclaw/aible-traces/current/outputs.json\`. → Proceed to Step 3.

**\`document_set_id\` branch:**
Call \`aible_summarize_document(doc_id=document_set_id, query, blueprint_id)\`.
Run \`cat ~/.openclaw/aible-traces/current/outputs.json\`. → Proceed to Step 3.

**\`dataset_id\` branch:**
Call \`aible_get_dataset_info(dataset_id)\`, read outputs.json, then call the matching tool:

- \`aible_dataset_nlq(dataset_id, query, blueprint_id)\`
- \`aible_analyze_dataset(dataset_id, query, blueprint_id)\` — only if target variable is non-None
- \`aible_whats_changed(dataset_id, query, blueprint_id)\` — only if \`whats_changed_compatible\` is true

Run \`cat ~/.openclaw/aible-traces/current/outputs.json\`. → Proceed to Step 3.

**Rules:**
- Always pass \`blueprint_id\` from the same hit row — do not fabricate it.
- For \`prediction_tool\` on the blueprint path, \`blueprint_id\` is mandatory alongside \`project_id\`.
- Do not pass \`blueprint_id\` to any tool unless it came from a valid blueprint hit.

---

### Step 2b: Fallback path (no relevant blueprint)

Fall back to \`search_context\` when:
- \`blueprint_search\` returned an empty list or errored
- No hit is relevant to the user\'s question
- The relevant hit has zero or multiple id fields (invalid row)
- For prediction: no hit has \`project_id\`

Call \`aible_search_context\` with 10+ keywords from the user query.

\`\`\`
cat ~/.openclaw/aible-traces/current/outputs.json
\`\`\`

Extract every \`dataset_id\` and \`document_set_id\` from the \`search_context\`
output. Use only these IDs — do not guess.

For each \`dataset_id\`, call \`aible_get_dataset_info\`, then read outputs.json
and choose the appropriate tool (no \`blueprint_id\`):

- \`aible_dataset_nlq(dataset_id, query)\`
- \`aible_analyze_dataset(dataset_id, query)\` — only if target variable is non-None
- \`aible_whats_changed(dataset_id, query)\` — only if \`whats_changed_compatible\` is true
- \`aible_summarize_document(doc_id, query)\` — for document sets

For prediction without a blueprint: use \`get_dataset_info\` to find
\`trained_model_id\`, then call \`aible_prediction_tool(project_id=trained_model_id, query)\`.

After the analysis call, **run \`cat ~/.openclaw/aible-traces/current/outputs.json\`** and read the result, then proceed to Step 3.

---

### Step 3: Submit

Before writing the report, run:
\`\`\`
cat ~/.openclaw/aible-traces/current/outputs.json
\`\`\`
Read the full output history. Your report must quote or reference specific
findings from outputs.json — not paraphrased from memory, not invented.

Call \`aible_submit_final_report\` with the collection_id, the user\'s original
question as \`query\`, and your synthesised report.

Do NOT respond to the user before calling this tool.
`;
